import { Box, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { LoadMore } from 'components/LoadMore';
import { ROOT_MARGIN } from 'utils/constants';
import { SelectOption } from 'generated/graphql';
import SearchAddResult from './SearchAddResult';
import SearchListResults from './SearchListResults';

const SearchResultsPanel = ({
  inputValue,
  filteredOptions,
  isFetching,
  hasNextPage,
  isFetchingMore,
  loadMoreIndex,
  maxHeight,
  isFilling = false,
  isAddPinned = false,
  onResultClick,
  onAdd,
  onLoadMore,
}: {
  inputValue: string;
  filteredOptions: SelectOption[];
  isFetching: boolean;
  hasNextPage: boolean;
  isFetchingMore: boolean;
  loadMoreIndex?: number;
  maxHeight?: string;
  isFilling?: boolean;
  isAddPinned?: boolean;
  onResultClick: (option: string, optionEntityID: string) => void;
  onAdd?: () => void;
  onLoadMore: () => void;
}) => {
  const [listElement, setListElement] = useState<HTMLElement | null>(null);

  const loader = (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'center', gap: 1, py: 3 }}>
      <CircularProgress size={16} color="inherit" />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Searching…
      </Typography>
    </Stack>
  );
  const isFirstSearch = isFetching && filteredOptions.length === 0;
  const addResult = onAdd && <SearchAddResult inputValue={inputValue} onAdd={onAdd} />;
  const results = (
    <>
      <SearchListResults
        filteredOptions={filteredOptions}
        inputValue={inputValue}
        onOptionClick={onResultClick}
        loadMoreTriggerIndex={loadMoreIndex ?? filteredOptions.length - 1}
        loadMoreTrigger={
          hasNextPage && (
            <LoadMore
              isFetching={isFetchingMore}
              hasNextPage={hasNextPage}
              onEndReached={onLoadMore}
              rootMargin={ROOT_MARGIN}
              root={listElement}
            />
          )
        }
      />
      {isFetchingMore && <LinearProgress aria-label="Loading more results" />}
    </>
  );
  const dimWhileFetching = {
    opacity: isFetching ? 0.55 : 1,
    pointerEvents: isFetching ? 'none' : 'auto',
    transition: 'opacity 120ms ease-out',
  } as const;

  if (isAddPinned) {
    return (
      <Box sx={isFilling ? { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 } : undefined}>
        <Box
          ref={setListElement}
          sx={{
            ...dimWhileFetching,
            ...(maxHeight && { maxHeight, overflowY: 'auto' }),
            ...(isFilling && { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }),
          }}
          aria-busy={isFetching}
        >
          {isFirstSearch ? loader : results}
        </Box>
        {addResult}
      </Box>
    );
  }

  if (isFirstSearch) return loader;
  return (
    <Box sx={dimWhileFetching} aria-busy={isFetching}>
      <Box ref={setListElement} sx={{ ...(maxHeight && { maxHeight, overflowY: 'auto' }) }}>
        {results}
        {!hasNextPage && addResult}
      </Box>
    </Box>
  );
};

export default SearchResultsPanel;
